/**
 * Turns leave requests into a dense month of *coverage* — how many people are
 * away on each calendar day, and how close that is to leaving the team short.
 *
 * The register asks "what did this person do?"; a leave dashboard asks the
 * opposite question, "who is missing, and when does that start to hurt?". Same
 * shape as buildAttendanceMonth (one entry per day, never a gap) so the two
 * strips read identically, but the cell value is a headcount, not a status.
 */

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const pad = (n) => String(n).padStart(2, "0");

export const toDayString = (year, month, day) => `${year}-${pad(month + 1)}-${pad(day)}`;
export const daysInMonth = (year, month) => new Date(Date.UTC(year, month + 1, 0)).getUTCDate();

const utcDayString = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
};

// A half day still removes someone from the morning or afternoon, so it counts
// as an absence for coverage. It is flagged so the tooltip can say which half.
const isHalf = (w) => w === "first-half" || w === "second-half";

/**
 * Fraction of the team away at which a day stops being routine. Expressed
 * against headcount so it scales with the org; when headcount is unknown these
 * fall back to absolute counts rather than silently reporting everything green.
 */
export const coverageTone = (count, headcount) => {
  if (!count) return "muted";
  if (headcount > 0) {
    const ratio = count / headcount;
    if (ratio >= 0.34) return "danger";
    if (ratio >= 0.2) return "warning";
    return "info";
  }
  if (count >= 5) return "danger";
  if (count >= 3) return "warning";
  return "info";
};

/**
 * @param year      full year
 * @param month     0-indexed
 * @param entries   /leave-request/calendar rows (any status; see `statuses`)
 * @param headcount active employees, used to scale the coverage thresholds
 * @param statuses  which statuses occupy a day — approved only, by default
 * @param today     used to mark today and split elapsed from upcoming
 */
export const buildLeaveMonth = ({
  year,
  month,
  entries = [],
  headcount = 0,
  statuses = ["Approved"],
  today = new Date(),
}) => {
  const allowed = new Set(statuses);
  const byDay = new Map();

  (entries || []).forEach((entry) => {
    if (!allowed.has(entry?.status)) return;
    (entry.days || []).forEach((d) => {
      const key = utcDayString(d.date);
      if (!key) return;
      if (!byDay.has(key)) byDay.set(key, []);
      byDay.get(key).push({
        employeeId: entry.employeeId,
        employee: entry.employee || "Unknown",
        leaveType: entry.leaveType || "Leave",
        leaveCode: entry.leaveCode || null,
        status: entry.status,
        window: d.window || "full-day",
        requestId: entry.id,
      });
    });
  });

  const todayString = toDayString(today.getFullYear(), today.getMonth(), today.getDate());
  const total = daysInMonth(year, month);
  const days = [];

  for (let d = 1; d <= total; d += 1) {
    const dayString = toDayString(year, month, d);
    const people = byDay.get(dayString) || [];
    days.push({
      dayString,
      dayNum: d,
      weekday: WEEKDAYS[new Date(Date.UTC(year, month, d)).getUTCDay()],
      count: people.length,
      halfDays: people.filter((p) => isHalf(p.window)).length,
      people,
      tone: coverageTone(people.length, headcount),
      isToday: dayString === todayString,
      isFuture: dayString > todayString,
    });
  }

  const busiest = days.reduce((a, b) => (b.count > a.count ? b : a), days[0]);
  const summary = {
    // distinct people away at any point this month, not day-instances
    peopleAway: new Set(
      days.flatMap((x) => x.people.map((p) => String(p.employeeId || p.employee)))
    ).size,
    // total days of leave consumed across the team
    dayCount: days.reduce((sum, x) => sum + x.count, 0),
    onLeaveToday: days.find((x) => x.isToday)?.count || 0,
    peakCount: busiest?.count || 0,
    peakDay: busiest?.count ? busiest.dayString : null,
    // days where enough of the team is away to be worth an approver's attention
    strainedDays: days.filter((x) => x.tone === "warning" || x.tone === "danger").length,
    headcount,
  };

  return { days, summary };
};

/**
 * A leave range as people say it out loud.
 *
 * The module-screens book: "Dates in the requests table read day-then-month
 * like everywhere else; the ISO form belongs in exports." A queue rendered as
 * 2026-08-26 → 2026-08-27 makes the reader parse a machine format to answer
 * "is that this week", which is the only question they had.
 *
 * The shared parts of a range are said once — "26–27 Aug 2026", not
 * "26 Aug 2026 – 27 Aug 2026" — because the repetition is what makes a range
 * hard to read at a glance.
 */
export const formatLeaveRange = (from, to) => {
  const parse = (v) => {
    if (!v) return null;
    const d = new Date(String(v).slice(0, 10));
    return Number.isNaN(d.getTime()) ? null : d;
  };
  const a = parse(from);
  if (!a) return null;
  const b = parse(to) || a;

  // Spelled out rather than taken from toLocaleDateString: en-GB renders
  // September as "Sept" under current ICU and "Sep" under older versions, so
  // the same build would read differently depending on the runtime.
  const SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const day = (d) => d.getUTCDate();
  const mon = (d) => SHORT[d.getUTCMonth()];
  const yr = (d) => d.getUTCFullYear();

  if (a.getTime() === b.getTime()) return `${day(a)} ${mon(a)} ${yr(a)}`;
  if (yr(a) !== yr(b)) return `${day(a)} ${mon(a)} ${yr(a)} – ${day(b)} ${mon(b)} ${yr(b)}`;
  if (mon(a) !== mon(b)) return `${day(a)} ${mon(a)} – ${day(b)} ${mon(b)} ${yr(b)}`;
  return `${day(a)}–${day(b)} ${mon(b)} ${yr(b)}`;
};

/**
 * Whether approved leave can still be called off.
 *
 * Only before its first day: once leave has started the row is a statement of
 * what happened rather than a decision still open. Compared as whole days, so
 * leave starting later today still counts as not yet begun.
 */
export const canCancel = (r) => {
  if (r?.status !== "Approved" || !r?.firstDay) return false;
  const start = new Date(String(r.firstDay).slice(0, 10));
  if (Number.isNaN(start.getTime())) return false;
  const now = new Date();
  const todayUTC = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return start.getTime() >= todayUTC;
};

